import { Cancel } from "@mui/icons-material";
import CheckIcon from "@mui/icons-material/Check";
import ActionButton from "controls/global/action-button";
import { Jacket } from "entities/UIModel";
import React from "react";
import BlockBoldIcon from "theme/icons/BlockBoldIcon";
import ReviseIcon from "theme/icons/ReviseIcon";
import UndoIcon from "theme/icons/UndoIcon";
import ViewPrintIcon from "theme/icons/ViewPrintIcon";
import { useConfigContext } from "utils/context/ConfigContextProvider";
import { useProfileSettingsCache } from "utils/context/ProfileSettingsContext";
import useCreateFile from "utils/custom-hooks/useCreateFile";
import useFieldDisabler from "utils/custom-hooks/useFieldDisabler";
import useFormWrapper from "utils/custom-hooks/useFormWrapper";
import { OrderStatusType, PERMISSIONS } from "utils/data/enum";

export enum ReviseAction {
  OpenRevise,
  PreviewRevise,
  SubmitRevise,
  CancelRevise,
}

interface ButtonsSectionProps {
  onIssueJacket?: any;
  onPreviewJacket?: any;
  onViewJacket?: any;
  onVoidJacket?: any;
  onUndoVoidJacket?: any;
  orderStatusTypeCode?: string;
  isDisabled: boolean;
  issueButtonDisabled: boolean;
  submitReviseDisabled: boolean;
  voidButtonDisabled: boolean;
  undoVoidButtonDisabled: boolean;
  isRevisable: boolean;
  revising: boolean;
  endorsementEditMode: boolean;
  disableEndorsementSubmit: boolean;
  handleEndorsementGridCancel: () => void;
  handleEndorsementGridSubmit: () => void;
  onReviseAction: (action: ReviseAction) => void;
  productIndex: number;
}

const JacketButtonSection = ({
  isDisabled,
  issueButtonDisabled,
  submitReviseDisabled,
  voidButtonDisabled,
  undoVoidButtonDisabled,
  isRevisable,
  revising,
  onIssueJacket,
  onPreviewJacket,
  onViewJacket,
  onVoidJacket,
  onUndoVoidJacket,
  orderStatusTypeCode,
  endorsementEditMode,
  disableEndorsementSubmit,
  handleEndorsementGridCancel,
  handleEndorsementGridSubmit,
  onReviseAction,
  productIndex,
}: ButtonsSectionProps) => {    
  const { featureConfig: { enableUndoVoidJacket } } = useConfigContext();
  const { getValues} = useFormWrapper()
  const { isFileReported } = useCreateFile();

  const currentJacket: Jacket = getValues(`jackets.${productIndex}`);
  const isTransmitted = currentJacket?.pricingDetail?.isTransmitted;
  
  // Permission
  const [{ profileSettings }] = useProfileSettingsCache();
  const permissionCodes = profileSettings?.userPermissionItemTypes as string[];
  const hasUndoVoidPermission = permissionCodes?.includes(PERMISSIONS.UNDOVOID_JACKET);

  const isViewVisible = orderStatusTypeCode === OrderStatusType.Issued;
  const isIssueVisible =
    !orderStatusTypeCode ||
    orderStatusTypeCode === OrderStatusType.Pending ||
    orderStatusTypeCode === OrderStatusType.Error;
  const isVoidVisible = orderStatusTypeCode === OrderStatusType.Issued;
  const isUndoVoidVisible = enableUndoVoidJacket && hasUndoVoidPermission && orderStatusTypeCode === OrderStatusType.Voided && isTransmitted === 0; 
  const isReviseButtonDisabled = useFieldDisabler("JACKETReviseButton");
  const isVoidButtonDisabled = useFieldDisabler("JACKETVoidButton");
  const isUndoVoidButtonDisabled = useFieldDisabler("JACKETUndoVoidButton") || isFileReported;
  
  // Endorsement Submit & Cancel must come on only for PPE (Post Policy Endorsement)
  if (orderStatusTypeCode === OrderStatusType.Issued && endorsementEditMode)
    return (
      <>
        <ActionButton
          color="secondary"
          onClick={handleEndorsementGridCancel}
          startIcon={<Cancel />}
          variant="outlined"
        >
          Cancel
        </ActionButton>
        <ActionButton
          color="primary"
          disabled={disableEndorsementSubmit}
          onClick={handleEndorsementGridSubmit}
          startIcon={<CheckIcon />}
          variant="outlined"
        >
          Submit
        </ActionButton>
      </>
    );

  return (
    <>
      {isRevisable && !revising && (
        <ActionButton
          color="secondary"
          disabled={
            isDisabled || submitReviseDisabled || isReviseButtonDisabled
          }
          variant="outlined"
          startIcon={<ReviseIcon />}
          onClick={() => {
            onReviseAction(ReviseAction.OpenRevise);
          }}
        >
          Revise
        </ActionButton>
      )}

      {isRevisable && revising && (
        <>
          <ActionButton
            color="primary"
            disabled={isDisabled}
            variant="outlined"
            startIcon={<CheckIcon />}
            onClick={() => {
              onReviseAction(ReviseAction.SubmitRevise);
            }}
          >
            Submit Revision
          </ActionButton>
          <ActionButton
            color="secondary"
            disabled={isDisabled}
            variant="outlined"
            startIcon={<Cancel />}
            onClick={() => {
              onReviseAction(ReviseAction.CancelRevise);
            }}
          >
            Cancel
          </ActionButton>
        </>
      )}

      {isIssueVisible && (
        <ActionButton
          color="primary"
          disabled={isDisabled || issueButtonDisabled}
          variant="outlined"
          startIcon={<CheckIcon />}
          onClick={onIssueJacket}
          name={`issueJacket-${productIndex}`}
        >
          Issue
        </ActionButton>
      )}

      {isVoidVisible && !revising && (
        <ActionButton
          color="secondary"
          variant="outlined"
          startIcon={<BlockBoldIcon />}
          onClick={onVoidJacket}
          disabled={voidButtonDisabled || isVoidButtonDisabled}
        >
          Void
        </ActionButton>
      )}

      {isUndoVoidVisible && (
        <ActionButton
          color="primary"
          variant="outlined"
          startIcon={<UndoIcon />}
          onClick={onUndoVoidJacket}
          disabled={undoVoidButtonDisabled || isUndoVoidButtonDisabled}
        >
          Undo Void
        </ActionButton>
      )}


      {isViewVisible && !revising && (
        <ActionButton
          color="primary"
          // disabled={isDisabled || submitReviseDisabled}          
          onClick={onViewJacket}
          startIcon={<ViewPrintIcon />}
          variant="outlined"
        >
          View/Print
        </ActionButton>
      )}
    </>
  );
};

export default JacketButtonSection;
